import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFrontendBuild } from '../utils/fetch-frontend-build.util';
import {
  FRONTEND_BUILD_QUERY_KEY,
  FRONTEND_UPDATE_COOLDOWN_MS,
  FRONTEND_UPDATE_INTERVAL_MS,
} from '../constants/frontend-update.constants';

/**
 * Checks the deployed build without surfacing transient failures to the user.
 * @param enabled - Whether this is a built application instead of a development server.
 * @returns The last successfully validated build, retained across failed checks.
 */
export const useFrontendBuild = (enabled: boolean) => {
  const lastAttempt = useRef(-Infinity);
  const query = useQuery({
    queryKey: FRONTEND_BUILD_QUERY_KEY,
    queryFn: ({ signal }) => {
      lastAttempt.current = Date.now();
      return fetchFrontendBuild(signal);
    },
    enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: FRONTEND_UPDATE_INTERVAL_MS,
    refetchIntervalInBackground: false,
    meta: { errorHandling: 'caller' },
  });
  const { refetch } = query;
  useEffect(() => {
    if (!enabled) return;
    let resumeTimeout: ReturnType<typeof setTimeout> | undefined;
    const onResume = () => {
      clearTimeout(resumeTimeout);
      resumeTimeout = undefined;
      if (document.visibilityState !== 'visible' || !navigator.onLine) {
        return;
      }
      const remainingCooldown =
        FRONTEND_UPDATE_COOLDOWN_MS - (Date.now() - lastAttempt.current);
      if (remainingCooldown > 0) {
        resumeTimeout = setTimeout(onResume, remainingCooldown);
        return;
      }
      void refetch({ cancelRefetch: false });
    };
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    document.addEventListener('visibilitychange', onResume);
    return () => {
      clearTimeout(resumeTimeout);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
      document.removeEventListener('visibilitychange', onResume);
    };
  }, [enabled, refetch]);
  return query.data;
};
